import { useState, useEffect, useCallback } from 'react';
import { Folder, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const FolderPermissions = ({ userId, userName, userRole, onClose }) => {
  const [allFolders, setAllFolders] = useState([]);
  const [assignedFolderIds, setAssignedFolderIds] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const foldersRes = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/drive/clients`
      );
      const assignedRes = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/assigned-folders`
      );

      if (foldersRes.ok && assignedRes.ok) {
        const foldersData = await foldersRes.json();
        const assignedData = await assignedRes.json();

        setAllFolders(foldersData.clients);
        const assignedIds = assignedData.assigned_folders.map(f => f.drive_folder_id);
        setAssignedFolderIds(assignedIds);
        setSelectedFolders(assignedIds);
      }
    } catch (error) {
      toast.error('Failed to load folder permissions');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleFolder = (folderId) => {
    if (selectedFolders.includes(folderId)) {
      setSelectedFolders(selectedFolders.filter(id => id !== folderId));
    } else {
      setSelectedFolders([...selectedFolders, folderId]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/assign-folders`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(selectedFolders)
        }
      );

      if (response.ok) {
        toast.success('Folder permissions updated successfully!');
        onClose();
      } else {
        toast.error('Failed to update permissions');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="folder-permissions">
      <div className="bg-brand/10 border border-brand/20 rounded-lg p-4">
        <p className="text-sm text-text-primary">
          <strong>Assign Drive Folders to {userName}</strong>
        </p>
        <p className="text-xs text-text-secondary mt-1">
          {userRole === 'Client' 
            ? 'Clients can only access their own folder (set automatically)'
            : `Select which client folders this ${userRole.replace('_', ' ')} can access`
          }
        </p>
      </div>

      {userRole === 'Client' ? (
        <div className="text-center py-8 text-text-secondary">
          <Folder className="mx-auto mb-4" size={48} />
          <p>Client users are automatically assigned to their own folder.</p>
          <p className="text-sm mt-2">No manual assignment needed.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="folder-list">
            {allFolders.map((folder) => {
              const isSelected = selectedFolders.includes(folder.drive_folder_id);
              
              return (
                <button
                  key={folder.drive_folder_id}
                  onClick={() => toggleFolder(folder.drive_folder_id)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                    isSelected
                      ? 'bg-brand/10 border-2 border-brand'
                      : 'bg-white/50 border border-border-subtle hover:bg-white/70'
                  }`}
                  data-testid={`folder-${folder.drive_folder_id}`}
                >
                  <div className="flex items-center gap-3">
                    <Folder 
                      className={isSelected ? 'text-brand' : 'text-brand'} 
                      size={24} 
                    />
                    <div className="text-left">
                      <h4 className="font-semibold text-text-primary">
                        {folder.folder_name}
                      </h4>
                      <p className="text-xs text-text-secondary">
                        {folder.client_name}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <Check className="text-brand" size={24} />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-border-subtle">
            <p className="text-sm text-text-secondary">
              {selectedFolders.length} of {allFolders.length} folders selected
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                data-testid="cancel-permissions"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-brand hover:bg-brand-hover text-white"
                data-testid="save-permissions"
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FolderPermissions;
